<template>
  <div class="settings-page">
    <h1>Settings</h1>
    <div v-if="auth.loading">Loading...</div>
    <div v-else-if="!auth.user">
      <p>Please log in via Telegram to manage your settings.</p>
      <RouterLink to="/login">Login</RouterLink>
    </div>
    <div v-else>
      <label
        >Countdown duration (seconds):
        <input type="number" v-model.number="countdown" min="10" max="600" step="10" />
      </label>
      <button @click="save">Save</button>
      <div v-if="saved" class="saved">Saved!</div>
    </div>

    <h2>Notifications</h2>
    <div class="notifications-section">
      <div
        class="toggle-row"
        :class="{ disabled: notifPermission === 'denied' }"
        :title="
          notifPermission === 'denied' ? 'Notifications are blocked in your browser' : undefined
        "
      >
        <label class="toggle-label">
          <input
            type="checkbox"
            v-model="notificationsEnabled"
            :disabled="notifPermission === 'denied'"
            @change="onNotifToggle"
          />
          Game event notifications
        </label>
      </div>
      <div
        class="toggle-row"
        :class="{ disabled: notifPermission !== 'granted' || !notificationsEnabled }"
      >
        <label class="toggle-label">
          <input
            type="checkbox"
            v-model="backgroundOnly"
            :disabled="notifPermission !== 'granted' || !notificationsEnabled"
          />
          Only notify when tab is in background
        </label>
        <p class="toggle-hint">
          When off, notifications fire even while you have the tab open (alongside on-screen
          toasts).
        </p>
      </div>
      <div
        class="toggle-row"
        :class="{ disabled: notifPermission !== 'granted' || !notificationsEnabled }"
      >
        <label class="toggle-label">
          <input
            type="checkbox"
            v-model="verbose"
            :disabled="notifPermission !== 'granted' || !notificationsEnabled"
          />
          Verbose (notify on all events, like Telegram)
        </label>
        <p class="toggle-hint">
          When on, also notifies on controller joins/leaves, reconnects, and minor status changes.
        </p>
      </div>
      <div class="notif-test-row">
        <button
          @click="sendTestNotification"
          :disabled="
            notifPermission !== 'granted' || !notificationsEnabled || testSecondsLeft !== null
          "
        >
          {{
            testSecondsLeft !== null ? `Firing in ${testSecondsLeft}…` : 'Send test notification'
          }}
        </button>
      </div>
      <div v-if="testTriggered" class="notif-troubleshoot">
        <strong>Not seeing it?</strong>
        <ul>
          <li>
            If <em>Only notify when tab is in background</em> is on, switch to another app during
            the countdown — notifications only fire when the tab is hidden.
          </li>
          <li>
            Check macOS System Settings → Notifications → your browser is allowed to send
            notifications.
          </li>
          <li>Make sure Focus / Do Not Disturb is off.</li>
          <li>
            Try unchecking <em>Only notify when tab is in background</em> — notifications should
            then appear even with the tab open.
          </li>
        </ul>
      </div>
      <p v-if="notifPermission === 'denied'" class="notif-blocked-hint">
        Notifications are blocked in your browser. To enable them, open your browser's site settings
        for this page and set "Notifications" to "Allow", then reload.
      </p>
      <p v-else-if="notifPermission === 'default'" class="notif-hint">
        Enable the toggle above to request notification permission.
      </p>
    </div>

    <h2>User Details</h2>
    <div>
      <copy-link v-if="link" :url="link">
        <template #headline>Control Link</template>
        Share this link to have others control your countdown
      </copy-link>
      <p v-else>A link would be here, but it is not loaded.</p>
    </div>

    <h2>Friends</h2>
    <p class="muted">
      Users who accessed your control link are added here. You can remove them anytime.
    </p>
    <div class="friends" v-if="friends.length">
      <div v-for="f in friends" :key="f.id" class="friend-card">
        <img :src="f.photo_url || ponyAvatar()" :alt="f.fullname || 'Friend'" />
        <div class="info">
          <div class="name">{{ f.fullname || 'Unknown' }}</div>
          <div class="username" v-if="f.username">@{{ f.username }}</div>
        </div>
        <button class="remove" @click="removeFriend(f.id)">Remove</button>
      </div>
    </div>
    <p v-else class="muted">
      No friends yet. Share your control link and anyone who opens it will appear here.
    </p>

    <h2>Debug</h2>
    <div v-if="auth.user || auth.payload">
      <p><strong>Name:</strong> {{ auth.user?.fullname }}</p>
      <p><strong>Telegram ID:</strong> {{ auth.user?.telegram_id }}</p>
      <p><strong>ID:</strong> {{ auth.user?.id }}</p>
      <div><strong>Payload:</strong></div>
      <pre>{{ auth.payload }}</pre>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useIntervalFn } from '@vueuse/core'
import { RouterLink } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { apiFetch } from '@/api'
import CopyLink from '@/components/CopyLink.vue'
import { useToastStore } from '@/stores/toast'
import { ponyAvatar } from '@/utils/images'
import type { UserOut } from '@/schemas/schemas.ts'
import { useNotifications } from '@/composables/useNotifications'

const toasts = useToastStore()
const auth = useAuthStore()
const {
  permission: notifPermission,
  notificationsEnabled,
  backgroundOnly,
  verbose,
  requestPermission,
  notify,
} = useNotifications()

const testSecondsLeft = ref<number | null>(null)
const testTriggered = ref(false)
const { pause: pauseTicker, resume: resumeTicker } = useIntervalFn(
  () => {
    if (testSecondsLeft.value === null) return
    testSecondsLeft.value--
    if (testSecondsLeft.value <= 0) {
      pauseTicker()
      testSecondsLeft.value = null
      notify('🔔 Test notification', { body: 'Notifications are working!', autoClose: 5000 })
    }
  },
  1000,
  { immediate: false },
)

function sendTestNotification() {
  testTriggered.value = true
  testSecondsLeft.value = 5
  resumeTicker()
}

async function onNotifToggle() {
  if (notificationsEnabled.value && notifPermission.value === 'default') {
    await requestPermission()
  }
  if (notificationsEnabled.value && notifPermission.value !== 'granted') {
    notificationsEnabled.value = false
  }
}
const countdown = ref(60)
const saved = ref(false)
const link = ref<string | null>(null)

const friends = ref<UserOut[]>([])

onMounted(async () => {
  if (!auth.user) await auth.loadMe()
  if (auth.user) {
    countdown.value = auth.user.countdown_seconds
    await Promise.all([loadLink(), loadFriends()])
  }
})

async function loadLink() {
  if (!auth.user) return
  const resp = await apiFetch<{ url: string }>('/api/me/control-link', { method: 'POST' })
  if (resp.ok) {
    const data = await resp.json()
    link.value = data.url
  }
}

async function loadFriends() {
  const resp = await apiFetch<UserOut[]>('/api/me/friends')
  if (resp.ok) {
    friends.value = await resp.json()
  }
}

async function removeFriend(friendId: number) {
  const resp = await apiFetch(`/api/me/friends/${friendId}`, { method: 'DELETE' })
  if (resp.ok) {
    friends.value = friends.value.filter((f) => f.id !== friendId)
    toasts.add('Friend removed', 'success')
  } else {
    toasts.add('Failed to remove friend', 'error')
  }
}

async function save() {
  const resp = await apiFetch('/api/me/settings?countdown_seconds=' + countdown.value, {
    method: 'PATCH',
  })
  if (resp.ok) {
    saved.value = true
    await auth.loadMe()
    setTimeout(() => (saved.value = false), 1500)
  }
}
</script>

<style lang="scss" scoped>
.settings-page {
  max-width: 640px;
  margin: 2rem auto;
  padding: 2rem;
  background: var(--color-background-soft);
  color: var(--color-text);
  border-radius: 1rem;
  border: 1px solid var(--color-border);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  h2 {
    margin-top: 2rem;
  }
  label {
    display: block;
    margin-bottom: 1rem;
  }
  input {
    margin-left: 0.5rem;
    padding: 0.5rem;
    border-radius: 0.5rem;
    background: var(--color-background);
    color: var(--color-text);
    border: 1px solid var(--color-border);
  }
  button {
    background: transparent;
    color: var(--color-text);
    border: 1px solid var(--color-border);
    padding: 0.4rem 0.8rem;
    border-radius: 0.5rem;
    cursor: pointer;
  }
  .saved {
    color: #27ae60;
    margin-top: 1rem;
  }
  .muted {
    color: var(--color-text-muted);
    font-style: italic;
  }
  .notifications-section {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
  }
  .toggle-row {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    padding: 0.5rem 0.6rem;
    border: 1px solid var(--color-border);
    border-radius: 0.5rem;
    background: var(--color-background);
    &.disabled {
      opacity: 0.5;
    }
  }
  .toggle-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 500;
    cursor: pointer;
    input[type='checkbox'] {
      cursor: pointer;
      accent-color: var(--color-accent, #3b82f6);
    }
    input[type='checkbox']:disabled {
      cursor: not-allowed;
    }
  }
  .toggle-hint {
    margin: 0;
    font-size: 0.8rem;
    color: var(--color-text-muted);
  }
  .notif-test-row {
    display: flex;
    padding: 0.25rem 0;
    button:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }
  .notif-troubleshoot {
    font-size: 0.85rem;
    color: var(--color-text-muted);
    border: 1px dashed var(--color-border);
    border-radius: 0.5rem;
    padding: 0.6rem 0.8rem;
    ul {
      margin: 0.35rem 0 0;
      padding-left: 1.25rem;
    }
    li {
      margin-bottom: 0.25rem;
    }
    li:last-child {
      margin-bottom: 0;
    }
  }
  .notif-blocked-hint {
    margin: 0.25rem 0 0;
    font-size: 0.85rem;
    color: #ef4444;
    font-style: italic;
  }
  .notif-hint {
    margin: 0.25rem 0 0;
    font-size: 0.85rem;
    color: var(--color-text-muted);
    font-style: italic;
  }
  .friends {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 0.75rem;
    margin-top: 0.75rem;
  }
  .friend-card {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    border: 1px solid var(--color-border);
    border-radius: 0.5rem;
    padding: 0.5rem;
    background: var(--color-background);
  }
  .friend-card img {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
  }
  .friend-card .info {
    flex: 1;
  }
  .friend-card .name {
    font-weight: 600;
  }
  .friend-card .username {
    font-size: 0.85rem;
    color: var(--color-text-muted);
  }
  .friend-card .remove {
    border-color: #ef4444;
    color: #ef4444;
  }
}
</style>
